import { createFileRoute } from "@tanstack/react-router";
import { MarketingPage, Section, ComparisonTable, Callout } from "@/components/marketing-page";
import { marketingHead, type FAQ } from "@/lib/seo";
import { MEASURED_AI_SURFACE_LIST } from "@/lib/providers";

const PATH = "/methodology";
const TITLE = "How AI Visibility Is Measured, Monroya's Full Methodology";
const DESCRIPTION =
  "The full methodology behind Monroya: repeated checks, how Named, Recommended and Cited are tracked, how a verified win is defined, branded-question scoring, model-update detection and saved answers.";

const FAQS: FAQ[] = [
  {
    q: "Which AI assistants does Monroya measure?",
    a: `Paid plans check all 6: ${MEASURED_AI_SURFACE_LIST}. The free plan checks Gemini and Perplexity.`,
  },
  {
    q: "Why ask each question several times?",
    a: "A single answer from an AI assistant can change minute to minute. We run every tracked question several times per check and look at what the assistant consistently says, not what it said once.",
  },
  {
    q: "What's the difference between Named, Recommended and Cited?",
    a: "Named means your business is mentioned in the answer. Recommended means the answer puts you forward as a choice. Cited means one of your pages is linked as a source. We track all three separately because the fix for each is different.",
  },
  {
    q: "How do you define a verified win?",
    a: "A win counts only when the same question on the same assistant names you consistently across repeated checks, not a single lucky answer.",
  },
  {
    q: "What's a branded question, and why score it separately?",
    a: "A branded question already includes your business name (for example, \"is [your company] good\"). We score those separately so your main numbers measure whether AI recommends you to buyers who have not heard of you yet.",
  },
  {
    q: "How do you detect a possible AI model update?",
    a: "If 30% or more of an account's tracked questions shift on one assistant in the same scan, or the same shift shows up across many accounts at once, we flag it as a possible model update rather than counting it as your win or loss.",
  },
  {
    q: "Can I see the actual AI answer behind a result?",
    a: "Yes. Every answer we collect is saved, so any result can be traced back to the exact AI answer that produced it.",
  },
];

export const Route = createFileRoute("/methodology")({
  head: () =>
    marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Methodology", path: PATH },
      ],
      faqs: FAQS,
    }),
  component: MethodologyPage,
});

function MethodologyPage() {
  return (
    <MarketingPage
      stamp="Technical reference"
      title="How Monroya measures AI visibility"
      dek="The companion to /how-it-works for teams who want the detail: repeated checks, how we define Named, Recommended and Cited, what counts as a verified win, and how we catch a possible AI model update."
      updated="October 2026"
      faqs={FAQS}
      related={[{ label: "The narrative version", to: "/how-it-works" }]}
    >
      <Section heading="Assistants checked" id="providers">
        <p>
          Paid plans check all 6 AI assistants below on every scan. The free
          plan checks Gemini and Perplexity.
        </p>
        <ComparisonTable
          headers={["Assistant", "On paid plans", "On the free plan"]}
          rows={[
            ["ChatGPT", "Yes", "No"],
            ["Claude", "Yes", "No"],
            ["Gemini", "Yes", "Yes"],
            ["Perplexity", "Yes", "Yes"],
            ["Google AI Overviews", "Yes", "No"],
            ["Google AI Mode", "Yes", "No"],
          ]}
        />
      </Section>

      <Section heading="Repeated checks" id="repeated-checks">
        <p>
          A single question asked once can return a different answer the next
          time, even on the same assistant. To make a result you can trust,
          every tracked question is asked several times per check. Your
          result reflects what an assistant consistently says, not a one-off
          answer.
        </p>
      </Section>

      <Section heading="Named, Recommended and Cited" id="named-recommended-cited">
        <p>
          Each answer is scored on three separate signals, because each one
          calls for a different fix:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            <strong className="text-ink">Named</strong> - your business is
            mentioned in the answer text.
          </li>
          <li>
            <strong className="text-ink">Recommended</strong> - the answer
            puts you forward as a choice the buyer should consider.
          </li>
          <li>
            <strong className="text-ink">Cited</strong> - one of your pages
            is linked as a source behind the answer.
          </li>
        </ul>
        <p>
          A business can be named without being recommended, and recommended
          without being cited. We keep the three apart instead of folding
          them into one score.
        </p>
      </Section>

      <Section heading="What counts as a verified win" id="verified-win">
        <p>
          A win is not a single good answer. A question counts as a verified
          win only when the same question, on the same assistant, names you
          consistently across repeated checks. If a win later slips, you get
          an alert.
        </p>
      </Section>

      <Section heading="Branded questions, scored separately" id="branded-questions">
        <p>
          Questions that already include your business name (for example,
          "is [your company] worth it") are tracked but scored on their own.
          They tell you how AI talks about people who already know you. Your
          main score is built from questions that do not name you, since
          those are the ones that show whether AI recommends you to buyers
          who have never heard of you.
        </p>
      </Section>

      <Section heading="Possible model update detection" id="model-updates">
        <p>
          AI assistants update their underlying models without notice, and
          that can shift answers across many businesses at once, independent
          of anything you did. We flag a scan as a possible model update
          when either is true:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            30% or more of an account's tracked questions shift on one
            assistant in the same scan, or
          </li>
          <li>
            the same shift shows up across many accounts at the same time.
          </li>
        </ul>
        <Callout label="Why this matters">
          When a shift looks like a model update, we call it out instead of
          counting it as a win or a loss you caused. We don&rsquo;t claim that
          publishing a page caused Google or an AI assistant to change its
          answer; we only report what the repeated checks show.
        </Callout>
      </Section>

      <Section heading="Saved answers" id="saved-answers">
        <p>
          Every answer we collect from an assistant is saved. Any result in
          your account, named, recommended or cited, links back to the exact
          saved answer that produced it, so nothing is a black box.
        </p>
      </Section>

      <Section heading="What we store" id="storage">
        <p>
          Per check: the saved assistant answers, which questions were
          named, recommended or cited, and the result history over time. Per
          account: your tracked questions and competitor setup.
        </p>
        <p>
          Your scan data is yours. We send your buyer questions to the AI
          assistants to read their answers, and we don&rsquo;t sell or share
          your account data.
        </p>
      </Section>
    </MarketingPage>
  );
}
